// src/hooks/useAudioStream.ts
import { useState, useEffect, useCallback } from "react";
import { AudioSource, downloadNormalizedAudio } from "../../api/file/audioApi";
import { AudioCacheManager } from "../../services/AudioCacheManager";
import type { UserUuid } from "../../types/identity";

interface UseAudioStreamProps {
	patientId: UserUuid;
	fileId: string;
	autoStart?: boolean;
	source?: AudioSource;
	onError: (message: string) => void;
	onSuccess: (message: string) => void;
}

interface UseAudioStreamReturn {
	audioUrl: string | null;
	loading: boolean;
	progress: number;
	error: string | null;
	cached: boolean;
	initialize: () => void;
}

export const useAudioStream = ({
	patientId,
	fileId,
	autoStart = false,
	source = "report",
	onError,
	onSuccess,
}: UseAudioStreamProps): UseAudioStreamReturn => {
	const [audioUrl, setAudioUrl] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [progress, setProgress] = useState(0);
	const [error, setError] = useState<string | null>(null);
	const [cached, setCached] = useState(false);

	const cacheManager = AudioCacheManager.getInstance();

	const initialize = useCallback(async () => {
		setLoading(true);
		setError(null);
		setProgress(0);

		try {
			await cacheManager.initialize();

			const cachedEntry = await cacheManager.get(fileId);

			if (cachedEntry) {
				setCached(true);
				const url = URL.createObjectURL(cachedEntry.blob);
				setAudioUrl(url);
				setProgress(100);
				onSuccess("Audio loaded from cache");
			} else {
				setCached(false);

				const blob = await downloadNormalizedAudio(patientId, fileId, (prog) => {
					setProgress(prog);
				}, source);

				await cacheManager.set({
					fileId,
					patientId,
					blob,
					timestamp: Date.now(),
					size: blob.size,
				});

				const url = URL.createObjectURL(blob);
				setAudioUrl(url);
				onSuccess("Audio downloaded successfully");
			}
		} catch (err) {
			// The raw AxiosError carries the bearer in `config.headers`; log the event and ids only.
			console.warn("audio.stream.load_failed", { file_id: fileId, source });
			const errorMessage = err instanceof Error ? err.message : "Failed to load audio";
			setError(errorMessage);
			onError(errorMessage);
		} finally {
			setLoading(false);
		}
	}, [patientId, fileId, source, cacheManager, onError, onSuccess]);

	useEffect(() => {
		if (autoStart) {
			initialize();
		}

		return () => {
			if (audioUrl) {
				URL.revokeObjectURL(audioUrl);
			}
		};
	}, [autoStart, initialize]);

	return {
		audioUrl,
		loading,
		progress,
		error,
		cached,
		initialize,
	};
};